import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { CONTACT } from "../../data/services";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/about", label: "About", id: "about" },
  { href: "https://registration.ec.ap.gov.in/ecSearch", label: "EC", id: "ec" },
  { href: "https://meebhoomi.ap.gov.in/", label: "1-B & Adangal", id: "adangal" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${scrolled || open ? "bg-[#070F1E]/85 backdrop-blur-xl border-b border-[#C5A059]/20 py-3" : "bg-transparent border-b border-transparent py-5"}`} data-testid="site-header">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <img src="/logo192.png" alt="Ridhanya Associates logo" className="h-11 w-11 rounded-full ring-1 ring-[#C5A059]/50 transition-transform duration-700 group-hover:rotate-[20deg]" />
          <span className="leading-none">
            <span className="block font-display text-xl sm:text-2xl text-[#F4EEE3] tracking-wide">Ridhanya</span>
            <span className="block font-mono text-[10px] tracking-[0.35em] text-[#D9BD7E] uppercase mt-1">Associates</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((n) => n.href ? (
            <a key={n.id} href={n.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-link-${n.id}`}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm tracking-wide text-[#E6E1D6] hover:text-white transition-colors duration-300">
              {n.label}<ArrowUpRight className="h-3 w-3 text-[#C5A059]" />
            </a>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-link-${n.id}`}
              className={({ isActive }) => `relative px-4 py-2 text-sm tracking-wide transition-colors duration-300 ${isActive ? "text-[#E8CF94]" : "text-[#E6E1D6] hover:text-white"}`}>
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && <motion.span layoutId="nav-dot" className="absolute left-1/2 -bottom-0.5 h-1 w-1 -translate-x-1/2 rounded-full bg-[#C5A059]" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${CONTACT.tel}`} data-testid="header-call-btn"
            className="hidden lg:inline-flex items-center gap-2 rounded-full border border-[#C5A059]/50 px-5 py-2.5 text-sm text-[#F4EEE3] transition-[background-color,color] duration-300 hover:bg-[#C5A059] hover:text-[#070F1E]">
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
          <button className="md:hidden h-11 w-11 grid place-items-center rounded-full border border-[#C5A059]/40 text-[#F4EEE3]" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#070F1E]" data-testid="mobile-nav">
            <div className="px-5 pt-6 pb-8 flex flex-col">
              {NAV.map((n, i) => (
                <motion.div key={n.id} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.08 * i }}>
                  {n.href ? (
                    <a href={n.href} target="_blank" rel="noopener noreferrer" data-testid={`mobile-nav-link-${n.id}`} className="flex items-center justify-between border-b border-[#C5A059]/15 py-4 font-display text-3xl text-[#F4EEE3]">
                      {n.label} <ArrowUpRight className="h-5 w-5 text-[#C5A059]" />
                    </a>
                  ) : (
                  <Link to={n.to} data-testid={`mobile-nav-link-${n.id}`} className="flex items-center justify-between border-b border-[#C5A059]/15 py-4 font-display text-3xl text-[#F4EEE3]">
                    {n.label} <ArrowUpRight className="h-5 w-5 text-[#C5A059]" />
                  </Link>
                  )}
                </motion.div>
              ))}
              <a href={`tel:${CONTACT.tel}`} className="mt-6 inline-flex items-center gap-2 text-[#E8CF94]" data-testid="mobile-call-link"><Phone className="h-4 w-4" /> {CONTACT.phone}</a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
